import React from "react"
import Moment from "moment"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import { GetApi } from "../../api/api"
import { Classification } from "../../api/types"
import Pending from "../../components/Pending"
import AppContext from "../../components/AppContext"
import Table, {
  AddButton,
  ActionPopover,
  ActionPopoverButton,
} from "../../components/Table"
import Button from "../../components/Button"

import EditModal from "./components/EditModal"

const editAndNewDisabled = true

interface RenderSuccess {
  classifications: Classification[]
}

type PendingType = Pending<RenderSuccess, any>

interface ClassificationsState {
  showEditModal: boolean
  disabled: boolean
  isNewClassification?: boolean
  editClassification?: Classification
}

class Classifications extends React.Component<{}, ClassificationsState> {
  public static contextType = AppContext
  private pendingRef: React.RefObject<PendingType>

  constructor(props: {}) {
    super(props)
    this.pendingRef = React.createRef<PendingType>()
    this.state = {
      showEditModal: false,
      disabled: false,
    }
  }

  public render() {
    const getData = async () => {
      try {
        const api = await GetApi()
        const classifications = await api.getClassifications()
        return {
          classifications,
        }
      } catch (error) {
        throw new Error(error as any)
      }
    }

    const renderSuccess = (props: RenderSuccess) => {
      const rowData = props.classifications.map((cc) => {
        return {
          icon: (
            <span className="d-flex justify-content-center">
              <i className="modus-icons">optimize</i>
            </span>
          ),
          name: cc.name,
          modified: cc.modifiedAt,
          action: <this.EditButtons cc={cc} />,
        }
      })

      const columnData = [
        {
          Header: "",
          accessor: "icon",
          width: 70,
          sortable: false,
        },
        {
          Header: "Name",
          accessor: "name",
        },
        {
          Header: "Modified",
          id: "modified",
          accessor: (row: any) => Moment(row.modified).format("x"),
          Cell: (row: any) => Moment(row.original.modified).format("lll"),
        },
        {
          Header: (
            <AddButton
              onClick={this.createClassification}
              disabled={this.state.disabled || editAndNewDisabled}
            />
          ),
          accessor: "action",
          width: 70,
          sortable: false,
        },
      ]

      return (
        <Row className="d-flex flex-fill justify-content-center">
          {this.state.showEditModal && (
            <EditModal
              ccId={this.state.editClassification!.id!}
              isOpen={this.state.showEditModal}
              toggle={this.toggleEditModal}
              headerText={
                this.state.isNewClassification
                  ? "New Classification"
                  : "Edit Classification"
              }
              toggleText={this.state.isNewClassification ? "Create" : "Done"}
              onCancel={
                this.state.isNewClassification
                  ? () =>
                      this.deleteClassification(this.state.editClassification!)
                  : undefined
              }
              size="xl"
            />
          )}
          {rowData.length > 0 ? (
            <Col xs={12} className="mb-3">
              <Table
                data={rowData}
                columns={columnData}
                defaultSorted={[
                  {
                    id: "modified",
                    desc: true,
                  },
                ]}
              />
            </Col>
          ) : (
            <Col sm={12} md={8} xl={5} className="my-auto text-center">
              <p className="display-4">Hi, there!</p>
              <p className="mb-4 text-muted">
                It seems like you haven't created a classification yet, press
                the button to get started with your first classification.
              </p>
              <Button variant="primary" onClick={this.createClassification}>
                Get started
              </Button>
            </Col>
          )}
        </Row>
      )
    }

    return (
      <Pending
        ref={this.pendingRef}
        getData={getData}
        renderSuccess={renderSuccess}
      />
    )
  }

  private EditButtons = (props: { cc: Classification }) => {
    const editClassification = () => this.toggleEditModal(props.cc!)
    const refreshClassification = () => this.refreshClassification(props.cc!)
    const deleteClassification = () => this.deleteClassification(props.cc!)

    return (
      <ActionPopover>
        <ActionPopoverButton
          onClick={editClassification}
          disabled={this.state.disabled || editAndNewDisabled}
        >
          <span>Edit</span>
        </ActionPopoverButton>
        <ActionPopoverButton
          onClick={refreshClassification}
          disabled={this.state.disabled}
        >
          <span>Refresh</span>
        </ActionPopoverButton>
        <div className="dropdown-divider" />
        <ActionPopoverButton
          variant="danger"
          onClick={deleteClassification}
          disabled={this.state.disabled}
        >
          <span>Delete</span>
        </ActionPopoverButton>
      </ActionPopover>
    )
  }

  private refreshClassification = async (cc: Classification) => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      await api.updateClassification(cc.id!)
      this.refresh()
      this.context.useToasts.addToast(
        `Successfully updated the classification '${cc.name!}'.`,
        "success",
        true
      )
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private createClassification = async () => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      const classification = await api.createClassification({
        structureKey: "",
        structureId: 0,
        language: "en",
        // structureKey: "nNn7OMxge52EFV0uhZEDQw==",
        // structureId: 28,
        // language: ''
      })
      this.toggleEditModal(classification, true)
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private deleteClassification = async (cc: Classification) => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      if (this.state.showEditModal === true) {
        this.toggleEditModal(cc, true)
        await api.deleteClassification(cc.id!)
        this.refresh()
      } else {
        if (
          window.confirm("Are you sure you want to delete this classification?")
        ) {
          await this.context.setPanelContent(undefined)
          await api.deleteClassification(cc.id!)
          this.refresh()
          this.context.useToasts.addToast(
            `Successfully deleted the classification '${cc.name!}'.`,
            "success",
            true
          )
        }
      }
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private toggleEditModal = (cc?: Classification, isNew: boolean = false) => {
    this.setState(
      (prevState) => ({
        isNewClassification: isNew,
        editClassification: cc,
        showEditModal: !prevState.showEditModal,
      }),
      () => {
        if (
          this.state.showEditModal === false &&
          !this.state.isNewClassification
        ) {
          this.refresh()
        }
      }
    )
  }

  private setDisabled = (value: boolean) => {
    this.setState({
      disabled: value,
    })
  }

  private refresh() {
    this.pendingRef?.current?.refresh()
  }
}

export default Classifications
